import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, GraduationCap, Briefcase, ShieldCheck, KeyRound } from 'lucide-react';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/pathways', label: 'Career Pathways' },
  { to: '/programs', label: 'Programs' },
  { to: '/services', label: 'How We Help' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const PORTALS = [
  {
    to: '/student-login',
    signup: '/student-signup',
    label: 'Student Portal',
    hint: 'Track your application',
    Icon: GraduationCap,
  },
  {
    to: '/recruiter-login',
    signup: '/recruiter-signup',
    label: 'Recruiter Portal',
    hint: 'Manage referrals & commissions',
    Icon: Briefcase,
  },
  {
    to: '/staff-login',
    signup: null,
    label: 'Staff Portal',
    hint: 'Counsellor & team access',
    Icon: ShieldCheck,
  },
  {
    to: '/admin-login',
    signup: null,
    label: 'Admin Portal',
    hint: 'Full platform administration',
    Icon: KeyRound,
  },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const portalRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (portalRef.current && !portalRef.current.contains(e.target)) {
        setPortalOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    setPortalOpen(false);
    setOpen(false);
  }, [location.pathname]);

  const bookConsultation = () => navigate('/contact');

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-black/5 transition-shadow ${scrolled ? 'shadow-sm' : ''}`}
      data-testid="marketing-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_student-pathfinder-21/artifacts/ujq9wzpi_True%20Global%20Logo.png"
              alt="True Global Education"
              className="h-10 w-auto"
            />
            <div className="hidden sm:block leading-none">
              <div className="font-serif-editorial text-lg text-brand-obsidian tracking-tight">True Global</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-brand-pine font-bold mt-1 font-sans-clean">Education</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 font-sans-clean">
            {NAV.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                    active ? 'text-brand-pine' : 'text-brand-obsidian/70 hover:text-brand-pine'
                  }`}
                >
                  {n.label}
                  {active && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-brand-coral rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {/* Sign In dropdown (desktop) */}
            <div className="hidden lg:block relative" ref={portalRef}>
              <button
                onClick={() => setPortalOpen((v) => !v)}
                data-testid="header-signin-toggle"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-obsidian/80 hover:text-brand-pine px-3 py-2 rounded-full transition-colors font-sans-clean"
              >
                Sign In
                <ChevronDown size={15} className={`transition-transform ${portalOpen ? 'rotate-180' : ''}`} />
              </button>

              {portalOpen && (
                <div
                  className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-black/5 overflow-hidden font-sans-clean"
                  data-testid="header-portal-menu"
                >
                  <div className="px-5 pt-4 pb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-brand-pine font-bold">
                      Portal Access
                    </div>
                  </div>
                  {PORTALS.map(({ to, label, hint, Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      data-testid={`portal-link-${to.replace('/', '')}`}
                      className="flex items-start gap-3 px-5 py-3 hover:bg-brand-mist transition-colors group"
                    >
                      <div className="mt-0.5 h-9 w-9 rounded-full bg-brand-pine/10 flex items-center justify-center group-hover:bg-brand-coral/15 transition-colors">
                        <Icon size={17} className="text-brand-pine group-hover:text-brand-coral-deep transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-brand-obsidian">{label}</div>
                        <div className="text-xs text-brand-obsidian/60">{hint}</div>
                      </div>
                    </Link>
                  ))}
                  <div className="border-t border-black/5 bg-brand-mist/60 px-5 py-3 flex items-center justify-between">
                    <span className="text-xs text-brand-obsidian/60">New here?</span>
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <Link
                        to="/student-signup"
                        data-testid="portal-signup-student"
                        className="text-brand-pine hover:text-brand-coral-deep"
                      >
                        Student Sign Up
                      </Link>
                      <span className="text-brand-obsidian/20">·</span>
                      <Link
                        to="/recruiter-signup"
                        data-testid="portal-signup-recruiter"
                        className="text-brand-pine hover:text-brand-coral-deep"
                      >
                        Recruiter Sign Up
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={bookConsultation}
              data-testid="header-book-consultation"
              className="hidden sm:inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors font-sans-clean"
            >
              Book a Free Consultation
            </button>
            <button
              className="lg:hidden text-brand-obsidian p-2"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="header-mobile-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-6 flex flex-col gap-1 font-sans-clean">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === n.to
                    ? 'bg-brand-pine/5 text-brand-pine'
                    : 'text-brand-obsidian/70 hover:bg-brand-mist'
                }`}
              >
                {n.label}
              </Link>
            ))}

            <div className="mt-4 pt-4 border-t border-black/5">
              <div className="px-3 text-[10px] uppercase tracking-[0.2em] text-brand-pine font-bold mb-2">
                Portal Access
              </div>
              {PORTALS.map(({ to, label, hint, Icon }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-portal-${to.replace('/', '')}`}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-brand-mist transition-colors"
                >
                  <div className="h-8 w-8 rounded-full bg-brand-pine/10 flex items-center justify-center">
                    <Icon size={15} className="text-brand-pine" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-brand-obsidian">{label}</div>
                    <div className="text-xs text-brand-obsidian/60">{hint}</div>
                  </div>
                </Link>
              ))}
              <div className="flex items-center gap-4 px-3 pt-3 text-xs font-semibold">
                <Link
                  to="/student-signup"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-signup-student"
                  className="text-brand-pine"
                >
                  Student Sign Up
                </Link>
                <span className="text-brand-obsidian/20">·</span>
                <Link
                  to="/recruiter-signup"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-signup-recruiter"
                  className="text-brand-pine"
                >
                  Recruiter Sign Up
                </Link>
              </div>
            </div>

            <button
              onClick={() => { setOpen(false); bookConsultation(); }}
              className="mt-4 bg-brand-coral text-white text-sm font-semibold px-5 py-3 rounded-full"
            >
              Book a Free Consultation
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
